import { Box, Button, Typography } from "@mui/material";
import CustomDialog from "../components/common/CustomDialog";
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axiosInstance from "../components/api/axiosInstance";
import axios from "axios";
import { ClientActions } from "../components/api/RequestUtil";
import { SUCCESS } from "../utils/constants";
import AlertService from "../components/common/Alert/alertService";
import SecureFields from "../components/common/SecureFields";
import PaymentSuccess from "../components/settings/account/PaymentSuccess";

const CardDetails = ({ open, title = "Enter Card Details", handleSaveTitle = "Submit", clientId, refetchData }) => {
  CardDetails.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    handleSaveTitle: PropTypes.string,
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    refetchData: PropTypes.func,
  };

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const [fieldsValid, setFieldsValid] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState("");
  const cardTokenRef = useRef(null);
  const fieldsValidRef = useRef(false);
  const pendingSubmitRef = useRef(false);
  const { success, error } = AlertService();

  // Fetch Eway API key for business owner when dialog opens
  useEffect(() => {
    if (open) {
      setApiKeyLoading(true);
      setApiKeyError("");

      let getEwayCreds;
      if (window.ReactNativeWebView && window.location && window.location.search) {
        // Mobile WebView context: use /api/v2 base URL and client endpoint
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const mobileAxios = axios.create({
          baseURL: process.env.REACT_APP_MOBILE_BACKEND_URL,
        });
        if (token) {
          mobileAxios.defaults.headers.common["Authorization"] = token;
        }
        getEwayCreds = () => mobileAxios.get("/clients/eway/cred-environment");
      } else {
        // Regular web: use default axiosInstance and v1 endpoint
        getEwayCreds = ClientActions.getEwayCredentialsEnvironment;
      }

      getEwayCreds()
        .then((res) => {
          // For mobile, backend returns { encryption_key, environment }
          // For web, may return userEwayCredentials object
          let encryptionKey = "";
          if (res.data?.encryption_key) {
            encryptionKey = res.data.encryption_key;
          } else if (res.data?.data?.userEwayCredentials?.encryptionKey) {
            encryptionKey = res.data.data.userEwayCredentials.encryptionKey;
          } else if (res.data?.data?.userEwayCredentials?.publicApiKey) {
            encryptionKey = res.data.data.userEwayCredentials.publicApiKey;
          } else if (res.data?.data?.encryptionKey) {
            encryptionKey = res.data.data.encryptionKey;
          } else if (res.data?.data?.publicApiKey) {
            encryptionKey = res.data.data.publicApiKey;
          }
          if (encryptionKey) {
            setApiKey(encryptionKey);
          } else {
            setApiKeyError("Could not retrieve Eway Public API key.");
          }
        })
        .catch((err) => {
          setApiKeyError(err?.response?.data?.message || "Failed to fetch Eway Public API key.");
        })
        .finally(() => {
          setApiKeyLoading(false);
        });
    } else {
      setApiKey("");
      setApiKeyError("");
    }
  }, [open]);

  // Handle token from SecureFields
  const handleSecureFieldsToken = (token) => {
    // If running inside a React Native WebView, post token to mobile app
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
      window.ReactNativeWebView.postMessage(token);
      return;
    }
    // Otherwise, store token for web submission
    cardTokenRef.current = token;
    const valid = window.isSecureFieldsValid && window.isSecureFieldsValid();
    setFieldsValid(valid);
    fieldsValidRef.current = valid;
    // If pendingSubmit is true and fields are valid, process payment and reset pendingSubmit
    if (pendingSubmitRef.current && valid && token) {
      pendingSubmitRef.current = false;
      handleSubmitInternal();
    }
  };

  // Internal handler for card submission
  const handleSubmitInternal = async () => {
    setLoader(true);
    try {
      const response = await axiosInstance.post(postUserEwayCred, { card_token: cardTokenRef.current, clientId });
      if (response.data.status === SUCCESS) {
        success(response.data?.message);
        setPaymentSuccess(true);
      } else {
        error(response.data?.message || "Failed to save card.");
      }
    } catch (err) {
      error(err?.response?.data?.message || "An error occurred while saving card.");
    } finally {
      setLoader(false);
      handleCloseModal();
    }
  };
  // Dialog close handler
  const handleCloseModal = () => {
    if (typeof handleClose === "function") {
      handleClose();
    }
    setPaymentSuccess(false);
  };
  // Handler for submit button click with retry logic
  const handleSubmit = () => {
    // Blur the currently focused element to trigger Secure Fields validation
    const activeElement = document.activeElement;
    if (activeElement) {
      activeElement.blur();
    }
    // Retry logic: up to 3 times, 100ms apart
    pendingSubmitRef.current = true;
    let attempts = 0;
    const maxAttempts = 3;
    const retryDelay = 100;
    const trySubmit = () => {
      if (pendingSubmitRef.current && fieldsValidRef.current && cardTokenRef.current) {
        pendingSubmitRef.current = false;
        handleSubmitInternal();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(trySubmit, retryDelay);
      } else if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        error("Card details are incomplete or invalid after multiple attempts. Please check your input.");
      }
    };
    setTimeout(trySubmit, retryDelay);
  };

  // Show loading/error for API key fetch
  if (apiKeyLoading && open) {
    return <Box p={2}>Loading payment fields...</Box>;
  }
  if (apiKeyError && open) {
    return (
      <Box p={2} color="error.main">
        {apiKeyError}
      </Box>
    );
  }

  // Separate rendering for mobile webview and web
  const isWebView = window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function";

  if (isWebView) {
    // Mobile WebView: SecureFields + explicit Submit/Cancel buttons inside a card-like Box
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(30, 32, 36, 0.55)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          transition: "background 0.2s",
        }}
      >
        {/* Card-like container for heading, SecureFields and buttons */}
        <Box
          sx={{
            width: "auto",
            maxWidth: 400,
            mx: "auto",
            boxShadow: 3,
            borderRadius: 3,
            background: "#fff",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 3,
            px: 4,
          }}
        >
          {/* Heading for mobile webview inside card */}
          <Box sx={{ width: "100%", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#000", textAlign: "left" }}>
              Enter Payment Details
            </Typography>
          </Box>
          <SecureFields apiKey={apiKey} onToken={handleSecureFieldsToken} open={open} />
          <Box display="flex" justifyContent="center" gap={2} marginTop={3} width="100%">
            <Button
              variant="contained"
              sx={{
                borderRadius: 1,
                background: "#013D29",
                color: "#fff",
                fontWeight: 600,
                flex: 1,
                px: 3,
                py: 1.25,
                boxShadow: 1,
                transition: "background 0.2s",
                "&:hover": {
                  opacity: 0.7,
                },
                "&:active": {
                  opacity: 0.7,
                },
                "&:focus": {
                  opacity: 0.7,
                },
              }}
              onClick={() => {
                if (fieldsValid && cardTokenRef.current) {
                  window.ReactNativeWebView.postMessage(cardTokenRef.current);
                }
              }}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 1,
                background: "#fff",
                color: "#013D29",
                fontWeight: 600,
                flex: 1,
                px: 3,
                py: 1.25,
                boxShadow: 1,
                border: "1px solid #013D29",
                transition: "background 0.2s",
                "&:hover": {
                  opacity: 0.7,
                },
                "&:active": {
                  opacity: 0.7,
                },
                "&:focus": {
                  opacity: 0.7,
                },
              }}
              onClick={() => {
                window.ReactNativeWebView.postMessage(JSON.stringify({ cancel: true }));
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Web: dialog and success modal
  return (
    <>
      <CustomDialog
        title={title}
        open={open}
        handleClose={handleCloseModal}
        handleSaveTitle={handleSaveTitle}
        loader={loader}
        onCloseButtonClick={handleCloseModal}
        onSaveButtonClick={handleSubmit}
        disabled={!fieldsValid || !cardTokenRef.current || loader}
      >
        <Box marginTop={1}>
          <SecureFields apiKey={apiKey} onToken={handleSecureFieldsToken} open={open} />
        </Box>
      </CustomDialog>
      {paymentSuccess && (
        <PaymentSuccess
          open={paymentSuccess}
          handleClose={() => {
            setPaymentSuccess(false);
            refetchData && refetchData();
          }}
        />
      )}
    </>
  );
};
//comment to trigger change detection
export default CardDetails;
